import React, { useState, useCallback, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Button,
  Text,
  useColorModeValue,
  Textarea,
  useToast,
  VStack,
  Input,
} from '@chakra-ui/react'
import { useCreateCommunity } from 'queries/community.query'
import Dropzone from '../../settings/components/Dropzone' // Assuming the correct path

export const CreateCommunityModal = ({ isOpen, onClose }) => {
  // State for non-file fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })

  // State for files and previews
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)

  const [formErrors, setFormErrors] = useState({})
  const toast = useToast()
  const { mutate: createNewCommunity, isPending } = useCreateCommunity()

  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  // --- Input Change Handler for Text Fields ---
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  // --- Dropzone File Change Handlers (Memoized for stability) ---

  const handleLogoChange = useCallback(file => {
    // Revoke previous blob URL if exists
    setLogoPreview(prevPreview => {
      if (prevPreview) {
        URL.revokeObjectURL(prevPreview)
      }
      setLogoFile(file)
      return file ? URL.createObjectURL(file) : null
    })
  }, [])

  const handlePhotoChange = useCallback(file => {
    // Revoke previous blob URL if exists
    setPhotoPreview(prevPreview => {
      if (prevPreview) {
        URL.revokeObjectURL(prevPreview)
      }
      setPhotoFile(file)
      return file ? URL.createObjectURL(file) : null
    })
  }, [])

  // --- Cleanup Effect ---
  // This runs when the modal closes or component unmounts to free up memory from blob URLs.
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
      }
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview)
      }
    }
  }, [logoPreview, photoPreview])

  const validateForm = () => {
    const errors = {}

    // Validate name: required and string
    if (!formData.name.trim()) {
      errors.name = 'Community name is required.'
    }

    // You can add file validation here if needed (e.g., file type, size)
    // Example:
    // if (logoFile && logoFile.size > 2048 * 1024) { errors.logo = 'Logo is too large (max 2MB).' }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateCommunity = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const dataToSend = new FormData()
    dataToSend.append('name', formData.name)
    dataToSend.append('description', formData.description)

    // Append files from dedicated state variables
    if (logoFile) {
      dataToSend.append('logo', logoFile)
    }
    if (photoFile) {
      dataToSend.append('photo', photoFile)
    }

    createNewCommunity(dataToSend, {
      onSuccess: () => {
        // Clear all states and close modal
        if (logoPreview) URL.revokeObjectURL(logoPreview)
        if (photoPreview) URL.revokeObjectURL(photoPreview)

        onClose()
        setFormData({
          name: '',
          description: '',
        })
        setLogoFile(null)
        setLogoPreview(null)
        setPhotoFile(null)
        setPhotoPreview(null)

        toast({
          title: 'Community Created.',
          description: 'The new community has been successfully added.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        toast({
          title: 'Failed to Create Community.',
          description:
            err.response?.data?.message ||
            'There was an error creating the community',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent as='form' onSubmit={handleCreateCommunity}>
        <ModalHeader>Create New Community</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!formErrors.name}>
              <FormLabel>Name</FormLabel>
              <Input
                type='text'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              {formErrors.name && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.name}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                name='description'
                value={formData.description}
                onChange={handleInputChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Logo (Optional)</FormLabel>
              <Dropzone
                title='Drag or click to upload logo'
                onFileChange={handleLogoChange}
                preview={logoPreview}
              />
              {formErrors.logo && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.logo}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Photo (Optional)</FormLabel>
              <Dropzone
                title='Drag or click to upload main photo'
                onFileChange={handlePhotoChange}
                preview={photoPreview}
              />
              {formErrors.photo && (
                <Text color='red.500' fontSize='sm' mt={1}>
                  {formErrors.photo}
                </Text>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme='blue' type='submit' isLoading={isPending} mr={3}>
            Create
          </Button>
          <Button colorScheme='gray' onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
